import api from './api';
import { CanvasItem, Moodboard, ReorderItem } from '../types/moodboard';

export const moodboardService = {
    getMoodboards: async (): Promise<Moodboard[]> => {
        const response = await api.get('/moodboards');
        // Tolera respostas 200 fora do envelope esperado ({ data: [...] }).
        // Sem isso, um 200 sem `data` resolveria undefined e quebraria o
        // `savedMoodboards.map` do filmstrip (derruba o app via ErrorBoundary).
        const data = (response.data as any)?.data;
        return Array.isArray(data) ? data : [];
    },

    // Página keyset (PERF-26) para o scroll infinito da tira do canvas.
    // Sem cursor => primeira página. `nextCursor` null quando não há mais.
    // Mesma guarda de array do #186 (um 200 fora do envelope não derruba o app).
    // A resposta chega em camelCase (humps): next_cursor -> nextCursor.
    getMoodboardsPage: async (
        params: { cursor?: string | null; limit?: number } = {},
    ): Promise<{ data: Moodboard[]; nextCursor: string | null; total: number }> => {
        const response = await api.get('/moodboards', {
            params: {
                limit: params.limit ?? 12,
                ...(params.cursor ? { cursor: params.cursor } : {}),
            },
        });
        const body = response.data as any;
        const data = body?.data;
        return {
            data: Array.isArray(data) ? data : [],
            nextCursor: body?.nextCursor ?? null,
            total: typeof body?.total === 'number' ? body.total : 0,
        };
    },

    getMoodboard: async (id: number): Promise<Moodboard> => {
        const response = await api.get(`/moodboards/${id}`);
        return (response.data as any).data;
    },

    // Sobe um arquivo do canvas e devolve a CHAVE S3, nunca a url assinada.
    // Duas razoes para existir:
    //  1) Uma imagem recem-adicionada vive no estado como data URL base64
    //     (FileReader.readAsDataURL). Mandar isso dentro do PUT do board faz o
    //     corpo crescer ~33% sobre o tamanho do arquivo e estourar o limite do
    //     proxy — o servidor so converte base64 -> S3 DEPOIS de receber o corpo,
    //     entao a conversao nunca chega a rodar num board grande.
    //  2) ImageResource.url e presigned e expira em 60 min; persistir a url
    //     deixaria a imagem morta no board quando a assinatura vencesse. A
    //     chave e o que o carregamento do board sabe reassinar.
    uploadCanvasImage: async (file: File): Promise<string> => {
        const formData = new FormData();
        formData.append('image_file', file);
        formData.append('status', 'uploaded');

        const response = await api.post('/images', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            // api.ts nao define timeout, e o default do axios e ilimitado: sem
            // isso um upload travado segura o save do board (e a fila de
            // auto-save) para sempre.
            timeout: 120_000,
        });

        const image = (response.data as any)?.data;
        const key = image?.s3Key ?? image?.s3_key;

        if (!key) {
            throw new Error('Upload da imagem do canvas nao retornou a chave S3.');
        }

        return key;
    },

    updateMoodboard: async (id: number, data: { name?: string; sortOrder?: number; stylePrompt?: string; moodboardImages?: CanvasItem[] }): Promise<Moodboard> => {
        const response = await api.put(`/moodboards/${id}`, data);
        return (response.data as any).data;
    },

    deleteMoodboard: async (id: number): Promise<void> => {
        await api.delete(`/moodboards/${id}`);
    },

    reorderMoodboards: async (items: ReorderItem[]): Promise<void> => {
        await api.post('/moodboards/reorder', { items });
    },

    attachTags: async (id: number, tagIds: number[]): Promise<Moodboard> => {
        const response = await api.post(`/moodboards/${id}/tags`, { tag_ids: tagIds });
        return (response.data as any).data;
    },

    detachTag: async (id: number, tagId: number): Promise<Moodboard> => {
        const response = await api.delete(`/moodboards/${id}/tags/${tagId}`);
        return (response.data as any).data;
    },

    syncTags: async (id: number, tagIds: number[]): Promise<Moodboard> => {
        const response = await api.put(`/moodboards/${id}/tags`, { tag_ids: tagIds });
        return (response.data as any).data;
    },

    exportAsBrandbook: async (id: number): Promise<{ taskId: number }> => {
        const response = await api.post(`/moodboards/${id}/export-brandbook`);
        return response.data as { taskId: number };
    },
};

export default moodboardService;
